import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { Box, Chip, Grid, Stack, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SectionCard } from '../../../components/Section';
import { treasurySetupFormFields } from '../../../forms/fields/formFields/treasurySetup';
import { treasurySetupSchema } from '../../../forms/validationSchema/formSchema/treasurySetup';
import { TREASURY_GROUP_CONTRIBUTION } from '../../../constants/treasury';
import { useAppDispatch } from '../../../redux/hooks';
import { useApproveCompanyForTreasuryMutation } from '../../../redux/features/treasury/treasury.apiSlice';
import { fetchPortfolios } from '../../../redux/modules/b2bThriftPlan/companyGroups/companyGroup.actions';
import { companyGroupSelector } from '../../../redux/modules/b2bThriftPlan/companyGroups/companyGroup.selectors';
import { getTreasuryErrorMessage } from '../../../utils/treasury';
import { PortfolioCards } from '../CompanyGroups/components/PortfolioCards';

type TreasurySetupStepProps = {
  companyId: number;
  onDone: () => void;
};

/**
 * Step 1 — the treasury group (its one product) and department. One call creates the treasury bank, group,
 * department and the PENDING setup; lockup, max cap and the rest are written by the backend.
 */
export const TreasurySetupStep = ({ companyId, onDone }: TreasurySetupStepProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { portfolios } = useSelector(companyGroupSelector);
  const [approveCompanyForTreasury, { isLoading: isSaving }] = useApproveCompanyForTreasuryMutation();

  useEffect(() => {
    dispatch(fetchPortfolios({}));
  }, [dispatch]);

  const { handleSubmit, values, handleChange, setFieldValue, touched, errors, isValid, dirty } = useFormik({
    initialValues: {
      [treasurySetupFormFields.groupName]: 'Treasury',
      [treasurySetupFormFields.externalPortfolioIds]: [] as number[],
      [treasurySetupFormFields.departmentName]: 'Treasury',
      [treasurySetupFormFields.departmentDescription]: '',
    },
    validationSchema: treasurySetupSchema,
    validateOnMount: true,
    onSubmit: async (formValues) => {
      try {
        const description = String(formValues[treasurySetupFormFields.departmentDescription] || '').trim();

        await approveCompanyForTreasury({
          companyId,
          payload: {
            group: {
              name: String(formValues[treasurySetupFormFields.groupName]).trim(),
              contribution: TREASURY_GROUP_CONTRIBUTION,
              externalPortfolioIds: formValues[treasurySetupFormFields.externalPortfolioIds] as number[],
            },
            department: {
              name: String(formValues[treasurySetupFormFields.departmentName]).trim(),
              ...(description ? { description } : {}),
            },
          },
        }).unwrap();
        enqueueSnackbar('Treasury created!', { variant: 'success' });
        onDone();
      } catch (error) {
        enqueueSnackbar(getTreasuryErrorMessage(error, 'Treasury creation error!'), { variant: 'error' });
      }
    },
  });

  const selectedPortfolioId = (values[treasurySetupFormFields.externalPortfolioIds] as number[])[0];

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <SectionCard
          title="Treasury Group"
          description="Lockup, max cap and the other group settings are written by the backend."
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                size="small"
                id={treasurySetupFormFields.groupName}
                name={treasurySetupFormFields.groupName}
                label="Name"
                value={values[treasurySetupFormFields.groupName]}
                onChange={handleChange}
                error={touched.groupName && !!errors.groupName}
                helperText={touched.groupName && errors.groupName}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard
          title="Product"
          description="Pick the group's one product — it can't be changed later."
          action={
            <Chip
              size="small"
              color={selectedPortfolioId ? 'success' : 'default'}
              variant={selectedPortfolioId ? 'filled' : 'outlined'}
              label={selectedPortfolioId ? '1 selected' : 'None selected'}
            />
          }
        >
          <PortfolioCards
            portfolios={portfolios ?? []}
            selectedPortfolioId={selectedPortfolioId}
            onSelect={(portfolioId) => setFieldValue(treasurySetupFormFields.externalPortfolioIds, [portfolioId])}
          />
        </SectionCard>

        <SectionCard title="Treasury Department">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                size="small"
                id={treasurySetupFormFields.departmentName}
                name={treasurySetupFormFields.departmentName}
                label="Name"
                value={values[treasurySetupFormFields.departmentName]}
                onChange={handleChange}
                error={touched.departmentName && !!errors.departmentName}
                helperText={touched.departmentName && errors.departmentName}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                id={treasurySetupFormFields.departmentDescription}
                name={treasurySetupFormFields.departmentDescription}
                label="Description"
                value={values[treasurySetupFormFields.departmentDescription]}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={isSaving}
            startIcon={<SaveIcon />}
            disabled={!isValid || !dirty || isSaving}
          >
            Create Treasury
          </LoadingButton>
        </Box>
      </Stack>
    </form>
  );
};
